import { Component, inject } from '@angular/core';
import { BasePageComponent } from '../../_shared/components/page/base-page.component';
import { AccountFilterComponent } from '../../_shared/components/filters/account-filter/account-filter.component';
import { DateFilterComponent } from '../../_shared/components/filters/date-filter/date-filter.component';
import { AccountFilterData, DateIntervalFilterData, WidgetFilterData } from '../../_shared/models';
import { DateInterval } from '../../_shared/enums';
import { CommonModule } from '@angular/common';
import { TotalAssetsComponent } from './components/widgets/total-assets/total-assets.component';
import { OperationalAssetsComponent } from './components/widgets/operational-assets/operational-assets.component';
import { FleetUtilisationComponent } from './components/widgets/fleet-utilisation/fleet-utilisation.component';
import { AssetsInMaintenanceComponent } from './components/widgets/assets-in-maintenance/assets-in-maintenance.component';
import { EmptyStateComponent } from '../../_shared/components/states/empty-state/empty-state.component';
import { ErrorStateComponent } from '../../_shared/components/states/error-state/error-state.component';
import { SearchComponent } from '../../_shared/components/filters/search/search.component';
import { isEmptyObject } from '../../_shared/helpers/object.helper';
import { buildDateFilterFromPageFilter } from '../../_shared/helpers/page-filter-state-helper';
import { FilterService } from '../../_shared/services/filter.service';
import { ErrorHandlerService } from '../../_shared/services/error-handler.service';
import { NgbDropdownModule, NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { OperationalStatus } from './enums/operational-status.enum';
import { AssetType } from '../_shared/enums/asset-type.enum';
import { AssetOverviewDetailsRequest } from './models/asset-overview-request.model';
import { Subject } from 'rxjs';
import { AssetOverviewService } from './services/asset-overview.service';
import { AssetOverviewDetailsResponse, OutstandingCompliances } from './models/asset-overview-response.model';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { getAssetTypeLabel } from '../_shared/helpers/asset-type-filter.helper';
import { ServiceTypes } from '../_shared/enums/service-type.enum';
import { MaintenanceModel } from '../_shared/enums/maintenance-model.enum';
import { ScheduleType } from '../_shared/enums/service-schedule-type.enum';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-asset-overview',
  standalone: true,
  imports: [
    CommonModule,
    NgbDropdownModule,
    AccountFilterComponent,
    DateFilterComponent,
    TotalAssetsComponent,
    OperationalAssetsComponent,
    FleetUtilisationComponent,
    AssetsInMaintenanceComponent,
    EmptyStateComponent,
    ErrorStateComponent,
    SearchComponent,
    InfiniteScrollModule,
    NgbPopover,
    TranslocoDirective
  ],
  templateUrl: './asset-overview.component.html',
  styleUrl: './asset-overview.component.scss'
})
export class AssetOverviewComponent extends BasePageComponent {
  // Declared first: the label maps below are field initializers that call this.transloco.
  private readonly transloco: TranslocoService = inject(TranslocoService);
  private assetOverviewService: AssetOverviewService = inject(AssetOverviewService);
  private filterService: FilterService = inject(FilterService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);

  isLoading: boolean = true;
  hasError: boolean = false;
  hasAccountFilterError: boolean = false;
  page: number = 1;
  pageSize: number = 20;
  totalPages: number = 0;
  totalCount: number = 0;
  accountFilter: AccountFilterData;
  dateFilter: DateIntervalFilterData;
  defaultInterval: DateInterval = DateInterval.Month;
  disabledIntervals: DateInterval[] = [DateInterval.Half];
  searchTerm: string = '';
  filteredAssetsDetails: AssetOverviewDetailsResponse[] = [];
  assetsDetails: AssetOverviewDetailsResponse[] = [];
  outstandingCompliances: OutstandingCompliances[] = [];

  private allAssetStatuses: OperationalStatus[] = [OperationalStatus.Operational, OperationalStatus.InMaintenance];
  private allAssetTypes: AssetType[] = [
    AssetType.Truck, AssetType.Plane, AssetType.Boat, AssetType.Trailer,
    AssetType.Car, AssetType.Motorbike, AssetType.Generator, AssetType.Wagon,
    AssetType.ATG, AssetType.YellowEquipment, AssetType.Container, AssetType.SkyVault, AssetType.Van
  ];

  assetStatus: OperationalStatus[] = [...this.allAssetStatuses];
  assetType: AssetType[] = [...this.allAssetTypes];
  assetStatusFilter = OperationalStatus;
  assetTypeFilter = AssetType;

  assetTypeOptions = [
    { label: this.transloco.translate('assetOverview.assetTypes.allAssets'), value: null },
    { label: this.transloco.translate('assetOverview.assetTypes.trucks'), value: this.assetTypeFilter.Truck },
    { label: this.transloco.translate('assetOverview.assetTypes.planes'), value: this.assetTypeFilter.Plane },
    { label: this.transloco.translate('assetOverview.assetTypes.boats'), value: this.assetTypeFilter.Boat },
    { label: this.transloco.translate('assetOverview.assetTypes.trailers'), value: this.assetTypeFilter.Trailer },
    { label: this.transloco.translate('assetOverview.assetTypes.cars'), value: this.assetTypeFilter.Car },
    { label: this.transloco.translate('assetOverview.assetTypes.motorbikes'), value: this.assetTypeFilter.Motorbike },
    { label: this.transloco.translate('assetOverview.assetTypes.generators'), value: this.assetTypeFilter.Generator },
    { label: this.transloco.translate('assetOverview.assetTypes.wagons'), value: this.assetTypeFilter.Wagon },
    { label: this.transloco.translate('assetOverview.assetTypes.atg'), value: this.assetTypeFilter.ATG },
    { label: this.transloco.translate('assetOverview.assetTypes.yellowEquipment'), value: this.assetTypeFilter.YellowEquipment },
    { label: this.transloco.translate('assetOverview.assetTypes.containers'), value: this.assetTypeFilter.Container },
    { label: this.transloco.translate('assetOverview.assetTypes.vans'), value: this.assetTypeFilter.Van }
  ];

  readonly assetStatusLabels: Record<OperationalStatus, string> = {
    [OperationalStatus.Operational]: this.transloco.translate('assetOverview.status.operational'),
    [OperationalStatus.InMaintenance]: this.transloco.translate('assetOverview.status.inMaintenance')
  };

  readonly severityLabels: Record<number, string> = {
    [ServiceTypes.Minor]: this.transloco.translate('assetOverview.serviceTypes.minor'),
    [ServiceTypes.Intermediate]: this.transloco.translate('assetOverview.serviceTypes.intermediate'),
    [ServiceTypes.Major]: this.transloco.translate('assetOverview.serviceTypes.major')
  };

  readonly maintenanceModelLabels: Record<number, string> = {
    [MaintenanceModel.InHouse]: this.transloco.translate('assetOverview.maintenanceModels.inHouse'),
    [MaintenanceModel.Contract]: this.transloco.translate('assetOverview.maintenanceModels.contract')
  };

  readonly scheduleTypeLabels: Record<number, string> = {
    [ScheduleType.OEM]: this.transloco.translate('assetOverview.scheduleTypes.oem'),
    [ScheduleType.Custom]: this.transloco.translate('assetOverview.scheduleTypes.custom')
  };

  currentPopover: NgbPopover | null = null;
  closePopoverTimeout: any;

  private cancelSubject: Subject<void> = new Subject<void>();
  private hasAccountFilterInitialized = false;

  constructor() {
    super();
    this.accountFilter = {} as AccountFilterData;
    this.dateFilter = {} as DateIntervalFilterData;
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  onSearchTermUpdate(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.page = 1;
    this.assetsDetails = [];

    this.updatePageFilterState();
    this.getAssetOverviewDetails(true);
  }

  onAccountFilterChange(filter: AccountFilterData): void {
    this.hasAccountFilterInitialized = true;
    this.hasAccountFilterError = false;
    this.accountFilter = filter;
    this.updateFilter();
  }

  onAccountFilterLoadFailed(): void {
    this.hasAccountFilterInitialized = false;
    this.hasAccountFilterError = true;
    this.hasError = true;
    this.isLoading = false;
  }

  onDateFilterChange(filter: DateIntervalFilterData): void {
    this.dateFilter = filter;
    this.updateFilter();
  }

  updateFilter(): void {
    if (this.hasAccountFilterInitialized && !isEmptyObject(this.dateFilter) && !isEmptyObject(this.accountFilter)) {
      this.filterService.updateFilter({
        ...this.dateFilter,
        ...this.accountFilter
      } as WidgetFilterData);

      this.page = 1;
      this.assetsDetails = [];
      this.updatePageFilterState();
      this.getAssetOverviewDetails(true);
    }
  }

  get assetStatusLabel(): string {
    if (this.assetStatus.length === this.allAssetStatuses.length) return this.transloco.translate('assetOverview.filters.allStatuses');
    return this.assetStatusLabels[this.assetStatus[0]];
  }

  get assetTypeLabel(): string {
    if (this.assetType.length === this.allAssetTypes.length) return this.transloco.translate('assetOverview.assetTypes.allAssets');
    return getAssetTypeLabel(this.assetType[0], (key) => this.transloco.translate(key));
  }

  get showLastRowBorder(): boolean {
    return !this.isLoading &&
      this.assetsDetails?.length > 0 &&
      this.assetsDetails.length < this.pageSize;
  }

  async getAssetOverviewDetails(cancelPrevious: boolean = false): Promise<void> {
    if (!this.hasAccountFilterInitialized || this.hasAccountFilterError) return;
    this.isLoading = true;
    this.hasError = false;

    try {
      const request: AssetOverviewDetailsRequest = {
        accountIds: this.accountFilter.accountIds,
        timeFrom: this.dateFilter.startDate!,
        timeTo: this.dateFilter.endDate!,
        page: this.page,
        pageSize: this.pageSize,
        searchTerm: this.searchTerm,
        assetStatuses: this.assetStatus,
        assetTypes: this.assetType
      };

      if (cancelPrevious) {
        this.cancelSubject.next();
      }
      const response = await this.assetOverviewService.getAssetOverviewDetails(request, this.cancelSubject);
      const mappedData: AssetOverviewDetailsResponse[] = response.data?.results?.map(item => ({ ...item })) ?? [];

      this.totalPages = response.data?.totalPages ?? 0;
      this.totalCount = response.data?.totalCount ?? 0;
      this.assetsDetails = [...this.assetsDetails, ...mappedData];

      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.hasError = true;
          this.isLoading = false;
        }
      });
    }
  }

  async applyFilters(): Promise<void> {
    this.page = 1;
    this.assetsDetails = [];
    this.updatePageFilterState();

    await this.getAssetOverviewDetails(true);
  }

  async onAssetStatusFilter(assetStatus: OperationalStatus | null): Promise<void> {
    this.assetStatus = assetStatus === null ? [...this.allAssetStatuses] : [assetStatus];
    await this.applyFilters();
  }

  async onAssetTypeFilter(assetType: AssetType | null): Promise<void> {
    this.assetType = assetType === null ? [...this.allAssetTypes] : [assetType];
    await this.applyFilters();
  }

  onScrollDown(): void {
    if (this.isLoading || this.page >= this.totalPages) return;
    this.page++;
    this.getAssetOverviewDetails();
  }

  onAssetClick(assetId: number, assetName: string): void {
    this.router.navigate(['/asset-management/asset-overview/asset-details', assetId, assetName], { state: { assetId, assetName } });
  }

  shouldShowIcon(asset: AssetOverviewDetailsResponse): boolean {
    return asset.currentCompliances !== asset.totalCompliances;
  }

  getSeverityLabel(severity: ServiceTypes): string {
    return this.severityLabels[severity] ?? '-';
  }

  getMaintenanceModelLabel(model: MaintenanceModel): string {
    return this.maintenanceModelLabels[model] ?? '-';
  }

  getScheduleTypeLabel(type: ScheduleType): string {
    return this.scheduleTypeLabels[type] ?? '-';
  }

  getAssetStatus(status: OperationalStatus): string {
    if (status === OperationalStatus.InMaintenance) return this.transloco.translate('assetOverview.status.inMaintenance');
    return this.transloco.translate('assetOverview.status.operational');
  }

  getStatusBadgeClass(status: number): string {
    switch (status) {
      case OperationalStatus.InMaintenance:
        return 'status-badge--in-maintenance';
      case OperationalStatus.Operational:
        return 'status-badge--operational';
      default:
        return '';
    }
  }

  openPopover(popover: NgbPopover) {
    this.cancelClosePopover();

    if (this.currentPopover && this.currentPopover !== popover) {
      this.currentPopover.close();
    }

    this.currentPopover = popover;
    popover.open();
  }

  scheduleClosePopover(popover: NgbPopover) {
    this.closePopoverTimeout = setTimeout(() => {
      popover.close();
      this.currentPopover = null;
    }, 500);
  }

  cancelClosePopover() {
    if (this.closePopoverTimeout) {
      clearTimeout(this.closePopoverTimeout);
      this.closePopoverTimeout = null;
    }
  }

  protected override onRefresh(): void { }

  protected override shouldRefresh(): boolean { return false; }

  protected override getFilters(): any {
    return {
      ...this.dateFilter,
      ...this.accountFilter,
      searchTerm: this.searchTerm
    }
  }

  protected override applyPageFilterState(pageFilter: WidgetFilterData & { searchTerm?: string }): void {
    this.accountFilter = { accountIds: pageFilter.accountIds ?? [] };
    this.dateFilter = buildDateFilterFromPageFilter(this.dateFilter, {
      ...pageFilter,
      interval: pageFilter.interval ?? this.defaultInterval,
    });
    this.searchTerm = pageFilter.searchTerm ?? '';
  }
}